import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'zudoku/components';

import { headingSlug } from './headingSlug';

/**
 * « Nouveautés » on guide/nouveautes.mdx: static snapshot + live overlay.
 *
 * Each docs build runs `scripts/sync-nouveautes.mjs`, which writes the published
 * releases into the page as real markdown, so search, « Copier la page »,
 * heading anchors and the prerendered HTML all work like any other guide. That
 * markdown is wrapped in <LiveUpdates stamp="…">. In the browser we re-read the
 * feed; when it no longer matches the snapshot's stamp (a release was edited or
 * published in superhouse › Nouveautés since the last build), the live version
 * replaces the snapshot.
 *
 * The page has `toc: false`: Zudoku's « Sur cette page » is computed at build
 * time and could not follow the live releases. <PageMenu> replaces it, built
 * from the headings actually on screen, so it always matches what is shown.
 */

export type Item = {
  kind: 'feature' | 'fix';
  theme?: string;
  title: string;
  body: string;
  where?: string;
  guideLabel?: string;
  guideUrl?: string;
};

export type Release = {
  id: string;
  title: string;
  releasedOn: string;
  intro: string | null;
  items: Item[];
  updatedAt: string;
};

/** Same host family as the docs: dev docs read the dev API. */
function feedUrl(): string {
  const host = typeof window === 'undefined' ? '' : window.location.hostname;
  const dev = host.startsWith('dev-') || host === 'localhost' || host === '127.0.0.1';
  return `${dev ? 'https://dev-powerhouse.wearenoe.fr' : 'https://powerhouse.wearenoe.fr'}/v1/public/product-updates`;
}

/** Keep identical to `stampOf` in scripts/sync-nouveautes.mjs. */
export function stampOf(releases: Pick<Release, 'id' | 'updatedAt'>[]): string {
  const s = releases.map((r) => `${r.id}@${r.updatedAt}`).join('|');
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

/** Scroll to a section of this page. */
function goTo(id: string, smooth: boolean) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
}

/** Longest wait for the live feed before showing the snapshot anyway. */
const FEED_WAIT_MS = 3000;

export function LiveUpdates({ stamp, children }: { stamp: string; children: ReactNode }) {
  const [live, setLive] = useState<Release[] | null>(null);
  // Until the first feed check settles, a skeleton stands in for the content:
  // showing the snapshot and then swapping in newer releases made the page jump.
  const [ready, setReady] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    const settle = () => alive && setReady(true);
    const timer = window.setTimeout(settle, FEED_WAIT_MS);
    const load = () =>
      fetch(feedUrl(), { credentials: 'omit', cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((d: { releases: Release[] }) => {
          if (!alive) return;
          const changed = stampOf(d.releases) !== stamp;
          setLive((prev) =>
            !changed ? null : prev && stampOf(prev) === stampOf(d.releases) ? prev : d.releases,
          );
        })
        // Unreachable feed: keep what is shown (the build-time snapshot at first).
        .catch(() => undefined)
        .finally(settle);
    void load();
    // Back on the tab (e.g. after editing in superhouse): show the change without a reload.
    const onVisible = () => document.visibilityState === 'visible' && void load();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [stamp]);

  // A shared #link: once the content is shown, and again after a live swap
  // replaced the headings (same ids, see headingSlug).
  useEffect(() => {
    if (!ready || !window.location.hash) return;
    goTo(decodeURIComponent(window.location.hash.slice(1)), false);
  }, [live, ready]);

  return (
    <div ref={rootRef} className="noe-updates-root" aria-busy={!ready}>
      {ready && <PageMenu rootRef={rootRef} version={live ? stampOf(live) : stamp} />}
      {!ready && <UpdatesSkeleton />}
      {/* The snapshot stays in the HTML (search, no-JS) but hidden while loading. */}
      <div style={ready ? undefined : { display: 'none' }}>
        {live ? <ProductUpdates releases={live} /> : children}
      </div>
    </div>
  );
}

/** Placeholder while the latest releases load: a title, shortcuts and a few entries. */
function UpdatesSkeleton() {
  return (
    <div className="noe-updates-skeleton not-prose" role="status" aria-label="Chargement des nouveautés">
      <span className="noe-sk noe-sk-title" />
      <div className="noe-sk-row">
        {[90, 70, 110, 80].map((w) => (
          <span key={w} className="noe-sk noe-sk-chip" style={{ width: w }} />
        ))}
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="noe-sk-block">
          <span className="noe-sk noe-sk-heading" />
          <span className="noe-sk noe-sk-line" />
          <span className="noe-sk noe-sk-line" style={{ width: '85%' }} />
          <span className="noe-sk noe-sk-line" style={{ width: '60%' }} />
        </div>
      ))}
    </div>
  );
}

type MenuEntry = { id: string; label: string; depth: 0 | 1 };

/**
 * « Sur cette page », right of the content on wide screens (CSS hides it, and
 * shows the in-page shortcuts, when there is no room). Read from the DOM so it
 * lists exactly what is displayed, snapshot or live.
 */
function PageMenu({ rootRef, version }: { rootRef: React.RefObject<HTMLDivElement>; version: string }) {
  const [entries, setEntries] = useState<MenuEntry[]>([]);
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    // Every release title; the sections of the latest one only (keeps it short).
    const list: MenuEntry[] = [];
    let releaseIndex = -1;
    root.querySelectorAll<HTMLElement>('h2[id], h3[id]').forEach((el) => {
      const clean = (el.textContent ?? '').replace(/#\s*$/, '').trim();
      if (el.tagName === 'H2') {
        releaseIndex += 1;
        list.push({ id: el.id, label: clean, depth: 0 });
      } else if (releaseIndex === 0) {
        list.push({ id: el.id, label: clean, depth: 1 });
      }
    });
    setEntries(list);

    // Highlight the section being read (the last one whose top passed the header).
    const targets = list.map((e) => document.getElementById(e.id)).filter((t): t is HTMLElement => !!t);
    const onScroll = () => {
      let current: string | null = targets[0]?.id ?? null;
      for (const t of targets) if (t.getBoundingClientRect().top < 160) current = t.id;
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [rootRef, version]);

  if (entries.length < 2) return null;

  const onClick = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    history.replaceState(null, '', `#${id}`);
    goTo(id, true);
  };

  return (
    <nav className="noe-updates-toc not-prose" aria-label="Sur cette page">
      <div className="noe-updates-toc-inner">
        <p className="noe-updates-toc-title">Sur cette page</p>
        <ul>
          {entries.map((e) => (
            <li key={e.id} className={e.depth ? 'is-sub' : undefined}>
              <a
                href={`#${e.id}`}
                aria-current={active === e.id ? 'location' : undefined}
                onClick={(ev) => onClick(ev, e.id)}
              >
                {e.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

/**
 * Live rendering, same layout as the markdown the sync script writes: every
 * release open on the page, newest first. The latest keeps the plain section
 * ids (#tarifs…); older ones prefix theirs with the release so ids stay unique.
 */
function ProductUpdates({ releases }: { releases: Release[] }) {
  if (releases.length === 0) return <p>Aucune nouveauté pour l’instant.</p>;
  return (
    <div className="noe-updates">
      {releases.map((r, i) => (
        <ReleaseBlock key={r.id} release={r} idPrefix={i === 0 ? '' : `${headingSlug(r.title)}-`} />
      ))}
    </div>
  );
}

/** Same list as `indexMarkdown` in scripts/sync-nouveautes.mjs (narrow screens). */
function SectionIndex({ themes, hasFixes, idPrefix }: { themes: string[]; hasFixes: boolean; idPrefix: string }) {
  const labels = [...themes];
  if (hasFixes) labels.push('🐞 Corrections');
  if (labels.length < 2) return null;
  return (
    <div className="noe-updates-index">
      <p>
        {labels.map((l) => (
          <a key={l} href={`#${idPrefix}${headingSlug(l)}`}>
            {l}
          </a>
        ))}
      </p>
    </div>
  );
}

function ReleaseBlock({ release, idPrefix }: { release: Release; idPrefix: string }) {
  const features = release.items.filter((i) => i.kind === 'feature');
  const fixes = release.items.filter((i) => i.kind === 'fix');
  const themes = [...new Set(features.map((i) => i.theme ?? ''))];
  return (
    <section>
      <h2 id={headingSlug(release.title)}>📦 {release.title}</h2>
      <SectionIndex themes={themes} hasFixes={fixes.length > 0} idPrefix={idPrefix} />
      {release.intro && <p>{release.intro}</p>}
      {themes.map((theme) => (
        <div key={theme}>
          <h3 id={`${idPrefix}${headingSlug(theme)}`}>{theme}</h3>
          {features
            .filter((i) => (i.theme ?? '') === theme)
            .map((i, k) => (
              <p key={k}>
                <strong>{withPeriod(i.title)}</strong> {i.body}
                {(i.where || i.guideUrl) && <br />}
                {i.where && <em>📍 {i.where}</em>}
                {i.where && i.guideUrl && ' · '}
                {i.guideUrl && <GuideLink item={i} />}
              </p>
            ))}
        </div>
      ))}
      {fixes.length > 0 && (
        <>
          <h3 id={`${idPrefix}corrections`}>🐞 Corrections</h3>
          <ul>
            {fixes.map((i, k) => (
              <li key={k}>
                ✅ <strong>{i.title} :</strong> {i.body}
                {i.guideUrl && <> <GuideLink item={i} /></>}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function GuideLink({ item }: { item: Item }) {
  const label = `👉 ${item.guideLabel || 'Le guide'}`;
  return item.guideUrl?.startsWith('/') ? (
    <Link to={item.guideUrl}>{label}</Link>
  ) : (
    <a href={item.guideUrl} target="_blank" rel="noreferrer">
      {label}
    </a>
  );
}

function withPeriod(title: string): string {
  return /[.?!…]$/.test(title.trim()) ? title : `${title}.`;
}
